import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { Configuration } from "../../utils/configuration";

/**
 * Types
 */
export type ApiCallKinds = "get" | "delete" | "post" | "put" | "patch";
export type HeaderProps = {
  "X-Auth-Token"?: string;
  "X-UserOperation"?: string;
  "X-ERP"?: string;
  "X-Permissions"?: string;
  "X-Last-Erp-Bill-To"?: string;
  "X-Last-Erp-Ship-To"?: string;
  "X-Home-Branch-Id"?: string;
  "X-Last-Selected-Branch-Id"?: string;
  "X-User-ID"?: string;
  "Content-Type"?: string;
  "api-key"?: string;
  Authorization?: string;
  Accept?: string;
  deliveryType?: boolean;
  instructions?: string;
  poNumber?: string;
  paymentMethod?: string;
  shouldShipFullOrder?: boolean;
};
export type APICoreProps<Request = object> = {
  url: string;
  kind: ApiCallKinds;
  header: HeaderProps;
  body?: Request;
  hostOverride?: string;
  upload?: boolean;
  options?: AxiosRequestConfig;
  responseType?: "json" | "text" | "blob" | "arraybuffer";
};
export type SuccessResponse = {
  success: boolean;
};

/**
 * Config
 */
// istanbul ignore next
const host = Configuration.gatewayUrl || "";
const headerConfig = { "content-type": "application/json" };
const uploadConfig = { "content-type": "multipart/multipart/form-data" };
const otherConfig: AxiosRequestConfig = {
  maxBodyLength: Configuration.maxBodyWidth,
  maxContentLength: Configuration.maxContentWidth,
};

/**
 * Main API Call method
 * @param props - APICoreProps<Request>
 * @returns AxiosResponse<Response>
 */
export async function apiCall<Response, Request = object>(
  props: APICoreProps<Request>
) {
  // variables
  const config = {
    headers: { ...headerConfig, ...props.header },
    ...otherConfig,
    ...props.options,
  };
  const url = `${props.hostOverride ?? host}/${props.url}`;
  let data: Request | FormData | undefined = props.body;
  if (props.upload && props.body) {
    config.headers = { ...config.headers, ...uploadConfig };
    data = new FormData();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Object.entries(props.body).forEach(([key, val]: [string, any]) =>
      (data as FormData).append(key, val)
    );
  }
  // types
  type Res = AxiosResponse<Response>;
  // output
  switch (props.kind) {
    // GET
    case "get":
      return await axios.get<Request, Res>(url, config);
    // PATCH
    case "patch":
      return await axios.patch<Request, Res>(url, data ?? {}, config);
    // POST
    case "post":
      return await axios.post<Request, Res>(url, data ?? {}, config);
    // PUT
    case "put":
      return await axios.put<Request, Res>(url, data ?? {}, config);
    // DELETE
    case "delete":
      return await axios.delete<Request, Res>(url, { ...config, data });
  }
}
